import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Typography,
  Box,
  Chip,
  Grid,
  Card,
  CardContent,
  CircularProgress,
  Divider
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import moment from 'moment';

const ComprehensiveAIAnalysisModal = ({ open, onClose, aiData, loading }) => {
  if (loading) {
    return (
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <DialogContent sx={{ textAlign: 'center', py: 8 }}>
          <CircularProgress />
          <Typography sx={{ mt: 2 }}>Analyzing health parameters...</Typography>
        </DialogContent>
      </Dialog>
    );
  }

  if (!aiData) return null;

  const getRiskColor = (level) => {
    const colors = { low: '#2AAB6C', moderate: '#FF9800', high: '#f44336' };
    return colors[level?.toLowerCase()] || '#757575';
  };

  const getDurationLabel = (duration) => {
    const labels = { d: 'Day', w: 'Week', m: 'Month', '3m': '3 Months', '6m': '6 Months', y: 'Year' };
    return labels[duration] || duration;
  };

  const getStartDate = (endDate, duration) => {
    const end = moment(endDate);
    const durationMap = { d: [1, 'days'], w: [7, 'days'], m: [1, 'months'], '3m': [3, 'months'], '6m': [6, 'months'], y: [1, 'years'] };
    const [amount, unit] = durationMap[duration] || [7, 'days'];
    return end.subtract(amount, unit).format('MMM DD, YYYY');
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle sx={{
        background: 'linear-gradient(135deg, #2AAB6C 0%, #097EB7 100%)',
        color: 'white',
        pb: 2
      }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <Box>
            <Typography variant="h5" fontWeight="bold" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              🏥 Comprehensive Health Analysis
            </Typography>
            {aiData.duration && aiData.endDate && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 1.5, flexWrap: 'wrap' }}>
                <Chip
                  icon={<CalendarTodayIcon sx={{ fontSize: 16, color: 'white !important' }} />}
                  label={`${getDurationLabel(aiData.duration)} Analysis`}
                  sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white', fontWeight: 500 }}
                  size="small"
                />
                <Chip
                  icon={<TrendingUpIcon sx={{ fontSize: 16, color: 'white !important' }} />}
                  label={`${getStartDate(aiData.endDate, aiData.duration)} - ${moment(aiData.endDate).format('MMM DD, YYYY')}`}
                  sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white', fontWeight: 500 }}
                  size="small"
                />
              </Box>
            )}
          </Box>
          <IconButton onClick={onClose} sx={{ color: 'white' }}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ p: 3, bgcolor: '#f8f9fa' }}>
        {/* Risk Level */}
        <Box sx={{ mb: 3, textAlign: 'center', p: 2, bgcolor: 'white', borderRadius: 2, boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
          <Chip
            label={`Risk Level: ${aiData.risk_level?.toUpperCase()}`}
            sx={{
              bgcolor: getRiskColor(aiData.risk_level),
              color: 'white',
              fontWeight: 'bold',
              fontSize: '1.1rem',
              px: 3,
              py: 3.5,
              boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
            }}
          />
        </Box>

        {/* Overall Summary */}
        {aiData.overall_summary && (
          <Card sx={{ mb: 3, borderLeft: `5px solid #2AAB6C`, boxShadow: '0 2px 8px rgba(0,0,0,0.08)', borderRadius: 2 }}>
            <CardContent sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom fontWeight="bold" sx={{ display: 'flex', alignItems: 'center', gap: 1, color: '#2AAB6C' }}>
                📋 Overall Summary
              </Typography>
              <Divider sx={{ my: 1.5 }} />
              <Typography sx={{ lineHeight: 1.8 }}>{aiData.overall_summary}</Typography>
            </CardContent>
          </Card>
        )}

        {/* Trend Explanation */}
        <Card sx={{ mb: 3, borderLeft: `5px solid #097EB7`, boxShadow: '0 2px 8px rgba(0,0,0,0.08)', borderRadius: 2 }}>
          <CardContent sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom fontWeight="bold" sx={{ display: 'flex', alignItems: 'center', gap: 1, color: '#097EB7' }}>
              📈 Trends
            </Typography>
            <Divider sx={{ my: 1.5 }} />
            <Typography sx={{ lineHeight: 1.8 }}>{aiData.trend_explanation}</Typography>
          </CardContent>
        </Card>

        {/* Parameter Insights */}
        {aiData.parameter_insights && (
          <Card sx={{ mb: 3, borderLeft: `5px solid #9C27B0`, boxShadow: '0 2px 8px rgba(0,0,0,0.08)', borderRadius: 2 }}>
            <CardContent sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom fontWeight="bold" sx={{ display: 'flex', alignItems: 'center', gap: 1, color: '#9C27B0' }}>
                🔍 Parameter Insights
              </Typography>
              <Divider sx={{ my: 1.5 }} />
              <Grid container spacing={2}>
                {Object.entries(aiData.parameter_insights).map(([key, value]) => (
                  <Grid item xs={12} sm={6} key={key}>
                    <Box sx={{ p: 2.5, bgcolor: '#fff', borderRadius: 2, border: '1px solid #e0e0e0', boxShadow: '0 1px 4px rgba(0,0,0,0.05)' }}>
                      <Typography variant="subtitle2" fontWeight="bold" textTransform="capitalize" sx={{ mb: 1 }}>
                        {key.replace(/_/g, ' ')}
                      </Typography>
                      <Chip
                        label={value.concern_level}
                        size="small"
                        sx={{ bgcolor: getRiskColor(value.concern_level), color: 'white', mt: 0.5, mb: 1 }}
                      />
                      <Typography variant="body2" sx={{ mt: 1, mb: 0.5 }}>{value.status}</Typography>
                      <Typography variant="caption" color="text.secondary" display="block">{value.trend}</Typography>
                      {value.avg_systolic && (
                        <Typography variant="caption" display="block" sx={{ mt: 1, fontWeight: 500 }}>
                          Avg: {value.avg_systolic}/{value.avg_diastolic} mmHg
                        </Typography>
                      )}
                      {value.avg_bpm && (
                        <Typography variant="caption" display="block" sx={{ mt: 1, fontWeight: 500 }}>
                          Avg: {value.avg_bpm} BPM
                        </Typography>
                      )}
                      {value.avg_percentage && (
                        <Typography variant="caption" display="block" sx={{ mt: 1, fontWeight: 500 }}>
                          Avg: {value.avg_percentage}%
                        </Typography>
                      )}
                      {value.avg_level && (
                        <Typography variant="caption" display="block" sx={{ mt: 1, fontWeight: 500 }}>
                          Avg: {value.avg_level} mg/dL
                        </Typography>
                      )}
                      {value.avg_rate && (
                        <Typography variant="caption" display="block" sx={{ mt: 1, fontWeight: 500 }}>
                          Avg: {value.avg_rate} breaths/min
                        </Typography>
                      )}
                      {value.avg_temp && (
                        <Typography variant="caption" display="block" sx={{ mt: 1, fontWeight: 500 }}>
                          Avg: {value.avg_temp}°C
                        </Typography>
                      )}
                    </Box>
                  </Grid>
                ))}
              </Grid>
            </CardContent>
          </Card>
        )}

        {/* Data Summary */}
        {aiData.graph_data && (
          <Card sx={{ mb: 3, borderLeft: `5px solid #FF9800`, boxShadow: '0 2px 8px rgba(0,0,0,0.08)', borderRadius: 2 }}>
            <CardContent sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom fontWeight="bold" sx={{ display: 'flex', alignItems: 'center', gap: 1, color: '#FF9800' }}>
                📊 Data Summary
              </Typography>
              <Divider sx={{ my: 1.5 }} />
              <Grid container spacing={2}>
                {aiData.graph_data.blood_pressure?.length > 0 && (
                  <Grid item xs={12} sm={6} md={4}>
                    <Box sx={{ p: 2, bgcolor: '#fff', borderRadius: 2, border: '1px solid #e0e0e0', textAlign: 'center' }}>
                      <Typography variant="subtitle2" fontWeight="bold">Blood Pressure</Typography>
                      <Typography variant="h6" color="primary" sx={{ mt: 1 }}>
                        {aiData.graph_data.blood_pressure.length}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">readings</Typography>
                    </Box>
                  </Grid>
                )}
                {aiData.graph_data.heart_rate?.length > 0 && (
                  <Grid item xs={12} sm={6} md={4}>
                    <Box sx={{ p: 2, bgcolor: '#fff', borderRadius: 2, border: '1px solid #e0e0e0', textAlign: 'center' }}>
                      <Typography variant="subtitle2" fontWeight="bold">Heart Rate</Typography>
                      <Typography variant="h6" color="primary" sx={{ mt: 1 }}>
                        {aiData.graph_data.heart_rate.length}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">readings</Typography>
                    </Box>
                  </Grid>
                )}
                {aiData.graph_data.spo2?.length > 0 && (
                  <Grid item xs={12} sm={6} md={4}>
                    <Box sx={{ p: 2, bgcolor: '#fff', borderRadius: 2, border: '1px solid #e0e0e0', textAlign: 'center' }}>
                      <Typography variant="subtitle2" fontWeight="bold">SPO2</Typography>
                      <Typography variant="h6" color="primary" sx={{ mt: 1 }}>
                        {aiData.graph_data.spo2.length}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">readings</Typography>
                    </Box>
                  </Grid>
                )}
                {aiData.graph_data.blood_sugar?.length > 0 && (
                  <Grid item xs={12} sm={6} md={4}>
                    <Box sx={{ p: 2, bgcolor: '#fff', borderRadius: 2, border: '1px solid #e0e0e0', textAlign: 'center' }}>
                      <Typography variant="subtitle2" fontWeight="bold">Blood Sugar</Typography>
                      <Typography variant="h6" color="primary" sx={{ mt: 1 }}>
                        {aiData.graph_data.blood_sugar.length}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">readings</Typography>
                    </Box>
                  </Grid>
                )}
                {aiData.graph_data.respiratory_rate?.length > 0 && (
                  <Grid item xs={12} sm={6} md={4}>
                    <Box sx={{ p: 2, bgcolor: '#fff', borderRadius: 2, border: '1px solid #e0e0e0', textAlign: 'center' }}>
                      <Typography variant="subtitle2" fontWeight="bold">Respiratory Rate</Typography>
                      <Typography variant="h6" color="primary" sx={{ mt: 1 }}>
                        {aiData.graph_data.respiratory_rate.length}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">readings</Typography>
                    </Box>
                  </Grid>
                )}
                {aiData.graph_data.body_temperature?.length > 0 && (
                  <Grid item xs={12} sm={6} md={4}>
                    <Box sx={{ p: 2, bgcolor: '#fff', borderRadius: 2, border: '1px solid #e0e0e0', textAlign: 'center' }}>
                      <Typography variant="subtitle2" fontWeight="bold">Body Temperature</Typography>
                      <Typography variant="h6" color="primary" sx={{ mt: 1 }}>
                        {aiData.graph_data.body_temperature.length}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">readings</Typography>
                    </Box>
                  </Grid>
                )}
              </Grid>
            </CardContent>
          </Card>
        )}

        {/* Correlations */}
        {aiData.correlations?.length > 0 && (
          <Card sx={{ mb: 3, borderLeft: `5px solid #00bcd4`, boxShadow: '0 2px 8px rgba(0,0,0,0.08)', borderRadius: 2 }}>
            <CardContent sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom fontWeight="bold" sx={{ display: 'flex', alignItems: 'center', gap: 1, color: '#00bcd4' }}>
                🔗 Correlations
              </Typography>
              <Divider sx={{ my: 1.5 }} />
              {aiData.correlations.map((item, i) => (
                <Typography key={i} variant="body2" sx={{ mb: 1.5, lineHeight: 1.6 }}>• {item}</Typography>
              ))}
            </CardContent>
          </Card>
        )}

        {/* Key Concerns & Positive Indicators */}
        <Grid container spacing={2} sx={{ mb: 3 }}>
          {aiData.key_concerns?.length > 0 && (
            <Grid item xs={12} md={6}>
              <Card sx={{ borderLeft: `5px solid #f44336`, height: '100%', boxShadow: '0 2px 8px rgba(0,0,0,0.08)', borderRadius: 2 }}>
                <CardContent sx={{ p: 3 }}>
                  <Typography variant="h6" gutterBottom fontWeight="bold" sx={{ display: 'flex', alignItems: 'center', gap: 1, color: '#f44336' }}>
                    ⚠️ Key Concerns
                  </Typography>
                  <Divider sx={{ my: 1.5 }} />
                  {aiData.key_concerns.map((item, i) => (
                    <Typography key={i} variant="body2" sx={{ mb: 1.5, lineHeight: 1.6 }}>• {item}</Typography>
                  ))}
                </CardContent>
              </Card>
            </Grid>
          )}
          {aiData.positive_indicators?.length > 0 && (
            <Grid item xs={12} md={6}>
              <Card sx={{ borderLeft: `5px solid #4caf50`, height: '100%', boxShadow: '0 2px 8px rgba(0,0,0,0.08)', borderRadius: 2 }}>
                <CardContent sx={{ p: 3 }}>
                  <Typography variant="h6" gutterBottom fontWeight="bold" sx={{ display: 'flex', alignItems: 'center', gap: 1, color: '#4caf50' }}>
                    ✅ Positive Indicators
                  </Typography>
                  <Divider sx={{ my: 1.5 }} />
                  {aiData.positive_indicators.map((item, i) => (
                    <Typography key={i} variant="body2" sx={{ mb: 1.5, lineHeight: 1.6 }}>• {item}</Typography>
                  ))}
                </CardContent>
              </Card>
            </Grid>
          )}
        </Grid>

        {/* Advice */}
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} md={6}>
            <Card sx={{ borderLeft: `5px solid #2AAB6C`, height: '100%', boxShadow: '0 2px 8px rgba(0,0,0,0.08)', borderRadius: 2 }}>
              <CardContent sx={{ p: 3 }}>
                <Typography variant="h6" gutterBottom fontWeight="bold" sx={{ display: 'flex', alignItems: 'center', gap: 1, color: '#2AAB6C' }}>
                  💡 Lifestyle Advice
                </Typography>
                <Divider sx={{ my: 1.5 }} />
                {aiData.lifestyle_advice?.map((item, i) => (
                  <Typography key={i} variant="body2" sx={{ mb: 1.5, lineHeight: 1.6 }}>• {item}</Typography>
                ))}
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={6}>
            <Card sx={{ borderLeft: `5px solid #097EB7`, height: '100%', boxShadow: '0 2px 8px rgba(0,0,0,0.08)', borderRadius: 2 }}>
              <CardContent sx={{ p: 3 }}>
                <Typography variant="h6" gutterBottom fontWeight="bold" sx={{ display: 'flex', alignItems: 'center', gap: 1, color: '#097EB7' }}>
                  📋 Monitoring Advice
                </Typography>
                <Divider sx={{ my: 1.5 }} />
                {aiData.monitoring_advice?.map((item, i) => (
                  <Typography key={i} variant="body2" sx={{ mb: 1.5, lineHeight: 1.6 }}>• {item}</Typography>
                ))}
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Doctor Consultation */}
        {aiData.doctor_consultation && (
          <Card sx={{ borderLeft: `5px solid #9C27B0`, boxShadow: '0 2px 8px rgba(0,0,0,0.08)', borderRadius: 2 }}>
            <CardContent sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom fontWeight="bold" sx={{ display: 'flex', alignItems: 'center', gap: 1, color: '#9C27B0' }}>
                🩺 Doctor Consultation
              </Typography>
              <Divider sx={{ my: 1.5 }} />
              <Typography sx={{ lineHeight: 1.8 }}>{aiData.doctor_consultation}</Typography>
            </CardContent>
          </Card>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ComprehensiveAIAnalysisModal;
